import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col items-center gap-0.5 text-(--text-text-black-default)" };
const _hoisted_2 = { class: "text-xl leading-6 font-bold" };
const _hoisted_3 = { class: "text-center text-base leading-6 font-normal" };
const _hoisted_4 = { class: "flex flex-col gap-5" };
const _hoisted_5 = { class: "flex flex-col gap-4" };
const _hoisted_6 = { class: "w-1/2" };
const _hoisted_7 = { class: "w-1/2" };
const _hoisted_8 = { class: "flex flex-col gap-4" };
const _hoisted_9 = { class: "text-base leading-6 font-bold text-(--text-text-heading)" };
const _hoisted_10 = { class: "grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2" };
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button, FileUploadInput, Modal, SelectInput, Textarea } from "@eudonet/opal";
import { logger } from "@/infrastructure/services/LoggerService";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { useAppToast } from "@/shared/composables/useAppToast";
import { CONSENT_STATUS_ICON, CONSENT_STATUS_ICON_COLOR, } from "@/modules/consent/domain/entities/consentOverview";
import { declareConsents, uploadConsentProof, } from "@/modules/contact/infrastructure/adapters/contactConsentService";
import { CONSENT_COLLECTION_MODES, toConsentChannelType, } from "@/modules/contact/domain/entities/contactConsent";
/**
 * "Modifier les consentements" — the story's central screen: the justification is entered ONCE
 * (collection mode, comment, proof) and applies to every purpose changed below, then a single save
 * records the lot as one act with one history entry.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditConsentsModal',
    props: {
        modelValue: { type: Boolean },
        contactId: {},
        channelType: {},
        value: {},
        purposes: {}
    },
    emits: ["update:modelValue", "saved"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        /**
         * Backend error codes this form can actually provoke → the message the user reads. Without the map
         * the axios failure surfaces as "Request failed with status code 400", which is useless in a
         * French or Dutch UI.
         *
         * Deliberately short: R70 only asks for codes the end user can trigger from the form. The
         * validator's other codes are unreachable here — the address is a prop, the statuses and collection
         * modes come from fixed option lists, duplicates cannot occur (statuses are keyed by purpose code),
         * an empty batch is blocked by `canSave`, and the comment field caps at the server's own 2000. They
         * fall through to the generic save error rather than carrying six untriggerable translations.
         */
        const DECLARE_ERROR_KEYS = {
            "ConsentDeclaration.CollectionMode.Required": "contact.detail.consents.errors.collectionModeRequired",
            CONSENT_MATRIX_INVALID_ENTRY: "contact.detail.consents.errors.matrixInactive",
        };
        const PROOF_ERROR_KEYS = {
            "ConsentProof.ContentType.Unsupported": "contact.detail.consents.errors.proofUnsupported",
            "ConsentProof.Size.TooLarge": "contact.detail.consents.errors.proofTooLarge",
            "ConsentProof.Size.Empty": "contact.detail.consents.errors.proofEmpty",
        };
        const isOpen = computed({
            get: () => props.modelValue,
            set: (open) => emit("update:modelValue", open),
        });
        const collectionMode = ref("");
        const comment = ref("");
        const proofFile = ref(null);
        const statuses = ref({});
        const collectionModeError = ref("");
        const isSaving = ref(false);
        // Reopening the modal must not inherit the previous address's answers — the justification is
        // per-declaration, and a stale comment would be attached as evidence to the wrong consents.
        watch(() => [props.modelValue, props.value], ([open]) => {
            if (!open)
                return;
            collectionMode.value = "";
            comment.value = "";
            proofFile.value = null;
            collectionModeError.value = "";
            statuses.value = Object.fromEntries(props.purposes.map((p) => [p.code, p.status]));
        }, { immediate: true });
        const collectionModeOptions = computed(() => CONSENT_COLLECTION_MODES.map((mode) => ({
            value: mode,
            label: t(`contact.detail.consents.collectionMode.${mode}`),
        })));
        const STATUS_ORDER = ["OptedIn", "NotSet", "OptedOut"];
        const statusOptions = computed(() => STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`contact.detail.consents.status.${status}`),
            icon: CONSENT_STATUS_ICON[status],
            iconColor: CONSENT_STATUS_ICON_COLOR[status],
        })));
        /** Only what actually changed is sent — an untouched purpose is not part of the declaration. */
        const changedEntries = computed(() => props.purposes
            .filter((purpose) => statuses.value[purpose.code] !== purpose.status)
            .map((purpose) => ({ purpose: purpose.code, status: statuses.value[purpose.code] })));
        const canSave = computed(() => collectionMode.value !== "" && changedEntries.value.length > 0);
        function close() {
            isOpen.value = false;
        }
        async function save() {
            // Guard, not just a disabled button: the field is the one mandatory input, and the message has
            // to say which control is at fault rather than relying on the asterisk alone (RGAA).
            if (collectionMode.value === "") {
                collectionModeError.value = t("contact.detail.consents.errors.collectionModeRequired");
                return;
            }
            if (changedEntries.value.length === 0)
                return;
            isSaving.value = true;
            try {
                let proof;
                if (proofFile.value) {
                    try {
                        proof = await uploadConsentProof(proofFile.value);
                    }
                    catch (e) {
                        // Distinct from the declaration failure: nothing was recorded, and the message must point
                        // at the file rather than at the consents.
                        logger.error("Failed to upload consent proof", e);
                        toastError(resolveApiErrorKey(e, PROOF_ERROR_KEYS, "contact.detail.consents.errors.proofUpload"));
                        return;
                    }
                }
                // Captured before the await: the toast pluralizes on how many consents THIS save recorded,
                // not on whatever the reactive diff holds after the parent refreshes.
                const savedCount = changedEntries.value.length;
                const result = await declareConsents({
                    contactId: props.contactId,
                    // The write surface speaks the OPINK vocabulary: a phone's consent is written as "Sms".
                    channelType: toConsentChannelType(props.channelType),
                    value: props.value,
                    collectionMode: collectionMode.value,
                    entries: changedEntries.value,
                    comment: comment.value.trim() || undefined,
                    proof,
                });
                toastSuccess("contact.detail.consents.saveSuccess", { count: savedCount }, savedCount);
                emit("saved", result);
                close();
            }
            catch (e) {
                logger.error("Failed to declare consents", e);
                toastError(resolveApiErrorKey(e, DECLARE_ERROR_KEYS, "contact.detail.consents.saveError"));
            }
            finally {
                isSaving.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: isOpen.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((isOpen).value = $event)),
                size: "lg",
                "header-position": "center",
                "footer-position": "split",
                "data-testid": "edit-consents-modal"
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)("contact.detail.consents.editModal.title")), 1 /* TEXT */),
                        _createElementVNode("p", _hoisted_3, [
                            _createTextVNode(_toDisplayString(_unref(t)("contact.detail.consents.editModal.subtitle", { address: __props.value })) + " ", 1 /* TEXT */),
                            _cache[5] || (_cache[5] = _createElementVNode("br", null, null, -1 /* CACHED */)),
                            _createTextVNode(" " + _toDisplayString(_unref(t)("contact.detail.consents.editModal.requiredHint")), 1 /* TEXT */)
                        ])
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _unref(t)('contact.detail.consents.editModal.cancel'),
                        "data-testid": "consent-modal-cancel",
                        onClick: close
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        "icon-left": "ri-check-fill",
                        label: _unref(t)('contact.detail.consents.editModal.save'),
                        disabled: !canSave.value,
                        loading: isSaving.value,
                        "data-testid": "consent-modal-save",
                        onClick: save
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_4, [
                        _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, [
                                _createVNode(_unref(SelectInput), {
                                    modelValue: collectionMode.value,
                                    "onUpdate:modelValue": [
                                        _cache[0] || (_cache[0] = ($event) => ((collectionMode).value = $event)),
                                        _cache[1] || (_cache[1] = ($event) => (collectionModeError.value = ''))
                                    ],
                                    options: collectionModeOptions.value,
                                    label: _unref(t)('contact.detail.consents.editModal.collectionMode'),
                                    placeholder: _unref(t)('contact.detail.consents.editModal.collectionModePlaceholder'),
                                    error: collectionModeError.value,
                                    required: "",
                                    "full-width": "",
                                    "data-testid": "consent-collection-mode"
                                }, null, 8 /* PROPS */, ["modelValue", "options", "label", "placeholder", "error"])
                            ]),
                            _createElementVNode("div", _hoisted_7, [
                                _createVNode(_unref(FileUploadInput), {
                                    modelValue: proofFile.value,
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((proofFile).value = $event)),
                                    variant: "basic",
                                    accept: "image/png,image/jpeg",
                                    label: _unref(t)('contact.detail.consents.editModal.proof'),
                                    "browse-button-text": _unref(t)('contact.detail.consents.editModal.proofBrowse'),
                                    placeholder: _unref(t)('contact.detail.consents.editModal.proofPlaceholder'),
                                    "max-file-size": 10 * 1024 * 1024,
                                    "max-file-size-text": _unref(t)('contact.detail.consents.editModal.proofHint'),
                                    "file-type-error-text": _unref(t)('contact.detail.consents.errors.proofUnsupported'),
                                    "file-size-error-text": _unref(t)('contact.detail.consents.errors.proofTooLarge'),
                                    "data-testid": "consent-proof"
                                }, null, 8 /* PROPS */, ["modelValue", "label", "browse-button-text", "placeholder", "max-file-size-text", "file-type-error-text", "file-size-error-text"])
                            ]),
                            _createVNode(_unref(Textarea), {
                                modelValue: comment.value,
                                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((comment).value = $event)),
                                label: _unref(t)('contact.detail.consents.editModal.comment'),
                                placeholder: _unref(t)('contact.detail.consents.editModal.commentPlaceholder'),
                                "help-text": _unref(t)('contact.detail.consents.editModal.commentHint'),
                                "max-length": 2000,
                                "full-width": "",
                                height: "76px",
                                "data-testid": "consent-comment"
                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "help-text"])
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                            _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)("contact.detail.consents.editModal.purposes")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_10, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.purposes, (purpose) => {
                                    return (_openBlock(), _createBlock(_unref(SelectInput), {
                                        key: purpose.code,
                                        modelValue: statuses.value[purpose.code],
                                        "onUpdate:modelValue": ($event) => ((statuses.value[purpose.code]) = $event),
                                        options: statusOptions.value,
                                        label: purpose.label,
                                        "full-width": "",
                                        "data-testid": `consent-purpose-select-${purpose.code}`
                                    }, null, 8 /* PROPS */, ["modelValue", "onUpdate:modelValue", "options", "label", "data-testid"]));
                                }), 128 /* KEYED_FRAGMENT */))
                            ])
                        ])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue"]));
        };
    }
});
